import { Fragment, useEffect, useRef, useState } from "react";
import api, { euro, price, formatApiError, formatDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { UploadCloud, Plus, Trash2, X, FileText, ChevronDown, Filter, Archive } from "lucide-react";
import { ConfirmDialog } from "../components/ConfirmDialog";
import DateRange from "../components/DateRange";
import { formatVatRate, MATCH_LABELS, MATCH_COLORS } from "../lib/vat";

export default function Acquisti() {
  const [invoices, setInvoices] = useState([]);
  const [showManual, setShowManual] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importLog, setImportLog] = useState([]);
  const [matchLog, setMatchLog] = useState([]);
  const [pendingMerges, setPendingMerges] = useState([]);
  const [vatPurch, setVatPurch] = useState([]);
  const fileRef = useRef();
  const { user } = useAuth();
  const [cFilters, setCFilters] = useState([]);
  const [gFilters, setGFilters] = useState([]);
  const [cfNew, setCfNew] = useState("");
  const [discards, setDiscards] = useState([]);
  const [showDiscards, setShowDiscards] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [catMap, setCatMap] = useState({});
  const [catColors, setCatColors] = useState({});
  const [cats, setCats] = useState([]);
  const [filterCat, setFilterCat] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [deletingId, setDeletingId] = useState(null);
  const PAGE_SIZE = 25;
  const suppliers = Array.from(new Set(invoices.map((i) => i.supplier_name).filter(Boolean))).sort();
  const invMatch = (inv) => (!filterCat || (inv.lines || []).some((l) => catMap[l.normalized_name] === filterCat)) && (!supplierFilter || inv.supplier_name === supplierFilter) && (!from || (inv.invoice_date || "") >= from) && (!to || (inv.invoice_date || "") <= to);
  const loadCatMeta = () => {
    api.get("/catalog/products").then(({ data }) => setCatMap(Object.fromEntries(data.map((p) => [p.normalized_name, p.category])))).catch(() => {});
    api.get("/categories/colors").then(({ data }) => setCatColors(data)).catch(() => {});
    api.get("/categories").then(({ data }) => setCats(data)).catch(() => {});
  };
  const loadDiscards = () => api.get("/import/discards").then(({ data }) => setDiscards(data)).catch(() => {});
  const loadPendingMerges = () => api.get("/catalog/pending-merges").then(({ data }) => setPendingMerges(data)).catch(() => {});
  const loadVatPurch = () => api.get("/reports/vat-purchases").then(({ data }) => setVatPurch(data)).catch(() => {});
  const undoMerge = async (uid) => { try { await api.post(`/catalog/merge-undo/${uid}`); toast.success("Unione annullata"); loadPendingMerges(); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Impossibile annullare"); } };
  const doMerge = async (sid, tid) => { try { const { data } = await api.post(`/catalog/products/${sid}/merge-into/${tid}`); toast.success("Prodotti uniti", data.undo_id ? { action: { label: "Annulla", onClick: () => undoMerge(data.undo_id) } } : undefined); loadPendingMerges(); load(); } catch { toast.error("Errore nell'unione"); } };
  const dismissMerge = async (sid) => { try { await api.post(`/catalog/products/${sid}/dismiss-merge`); loadPendingMerges(); } catch { toast.error("Errore"); } };
  const mergeAll = async () => { try { const res = await Promise.all(pendingMerges.map((pm) => api.post(`/catalog/products/${pm.id}/merge-into/${pm.candidate_id}`))); const ids = res.map((r) => r.data.undo_id).filter(Boolean); toast.success(`${res.length} prodotti uniti`, ids.length ? { action: { label: "Annulla tutti", onClick: async () => { await Promise.all(ids.map((u) => api.post(`/catalog/merge-undo/${u}`).catch(() => {}))); toast.success("Unioni annullate"); loadPendingMerges(); load(); } } } : undefined); loadPendingMerges(); load(); } catch { toast.error("Errore nell'unione in blocco"); } };
  const dismissAll = async () => { try { await Promise.all(pendingMerges.map((pm) => api.post(`/catalog/products/${pm.id}/dismiss-merge`))); loadPendingMerges(); } catch { toast.error("Errore"); } };
  const exportMatches = async () => {
    try {
      const res = await api.post("/reports/import-matches.xlsx", { matches: matchLog }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = "esito_import.xlsx"; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Errore nell'export"); }
  };
  useEffect(() => {
    api.get("/company/import-filters").then(({ data }) => { setCFilters(data.patterns || []); setGFilters(data.global_patterns || []); }).catch(() => {});
    loadDiscards();
    loadPendingMerges();
    loadVatPurch();
    loadCatMeta();
  }, []);
  const saveCF = async (next) => {
    try { const { data } = await api.put("/company/import-filters", { patterns: next }); setCFilters(data.patterns); toast.success("Filtri azienda salvati"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const addCF = () => { if (!cfNew.trim()) return; saveCF([...cFilters, cfNew.trim()]); setCfNew(""); };
  const removeCF = (i) => saveCF(cFilters.filter((_, idx) => idx !== i));

  const load = () => api.get("/invoices").then(({ data }) => setInvoices(data));
  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); }, [filterCat, supplierFilter, from, to]);

  const uploadFiles = async (files) => {
    setUploading(true);
    let ok = 0;
    let newP = 0, updP = 0;
    const log = [];
    const mlog = [];
    let bySc = 0, byIc = 0, byDesc = 0, byNew = 0;
    const vatWarn = [];
    for (const f of files) {
      const form = new FormData();
      form.append("file", f);
      try {
        const { data } = await api.post("/invoices/upload-xml", form, { headers: { "Content-Type": "multipart/form-data" } });
        ok++;
        const cs = data.catalog_summary || {};
        newP += cs.new || 0; updP += cs.updated || 0;
        const mc = cs.match_counts || {};
        bySc += mc.supplier_code || 0; byIc += mc.internal_code || 0; byDesc += (mc.description || 0) + (mc.description_fuzzy || 0); byNew += mc.new || 0;
        (cs.vat_warnings || []).forEach((w) => vatWarn.push({ file: f.name, ...w }));
        (cs.matches || []).forEach((m) => mlog.push({ file: f.name, ...m }));
        (data.discarded_lines || []).forEach((d) => log.push({ file: f.name, line: d }));
      } catch (err) {
        toast.error(`${f.name}: ${formatApiError(err.response?.data?.detail)}`);
      }
    }
    if (ok) toast.success(`${ok} acquisto/i importati — ${newP} nuovi, ${updP} aggiornati · abbinati: ${bySc} per cod. fornitore, ${byIc} per cod. interno, ${byDesc} per descrizione`);
    if (ok) {
      try {
        const { data: miss } = await api.get("/settings/category-vat-missing");
        const nMiss = Object.values(miss || {}).reduce((s, n) => s + (n || 0), 0);
        if (nMiss > 0) {
          toast.message(`Ci sono ${nMiss} prodotti senza IVA. Vuoi applicare i default di reparto?`, {
            duration: 12000,
            action: {
              label: "Applica default",
              onClick: async () => {
                try { const { data: r } = await api.post("/settings/apply-category-vat"); toast.success(`Applicato: ${r.updated_purchase} IVA Acquisti, ${r.updated_sales} IVA Vendite`); load(); }
                catch { toast.error("Errore applicazione"); }
              },
            },
          });
        }
      } catch { /* ignore */ }
    }
    if (vatWarn.length) toast.warning(`${vatWarn.length} avvisi IVA: ${vatWarn.slice(0, 3).map((w) => `${w.product} (anagrafica ${w.anagrafica}%, fattura ${w.fattura}%)`).join("; ")}${vatWarn.length > 3 ? "…" : ""}`);
    if (mlog.length) setMatchLog(mlog);
    loadPendingMerges();
    if (log.length) { setImportLog(log); toast.message(`${log.length} righe scartate dai filtri import`); }
    setUploading(false);
    load();
    loadDiscards();
    loadCatMeta();
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    uploadFiles([...e.dataTransfer.files].filter((f) => f.name.toLowerCase().endsWith(".xml")));
  };

  const del = async (id) => {
    if (deletingId) return;
    setDeletingId(id);
    try {
      await api.delete(`/invoices/${id}`);
      toast.success("Acquisto eliminato");
    } catch (err) {
      if (err.response?.status === 404) toast.message("Acquisto già rimosso");
      else toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setDeletingId(null);
      load();
    }
  };

  const filteredInvoices = invoices.filter(invMatch);
  const totalPages = Math.max(1, Math.ceil(filteredInvoices.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageInvoices = filteredInvoices.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Documenti</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-stone-900">Acquisti</h1>
          <p className="text-sm text-stone-500 mt-2">Ogni fattura importata genera i movimenti d'acquisto e aggiorna automaticamente l'Anagrafica Prodotti (fornitori e prezzi d'acquisto).</p>
        </div>
        <button data-testid="open-manual-btn" onClick={() => setShowManual(true)}
          className="rounded-full bg-brand text-white px-5 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-brand-hover transition-colors">
          <Plus size={16} /> Inserimento manuale
        </button>
      </div>

      <div
        data-testid="upload-zone"
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`border-2 border-dashed rounded-sm p-10 text-center cursor-pointer transition-colors ${
          dragging ? "border-brand bg-brand/5" : "border-stone-300 bg-stone-50 hover:bg-stone-100"
        }`}>
        <input ref={fileRef} type="file" accept=".xml" multiple hidden
          onChange={(e) => uploadFiles([...e.target.files])} data-testid="file-input" />
        <UploadCloud size={40} strokeWidth={1.3} className="mx-auto text-brand mb-3" />
        <p className="font-serif text-2xl text-stone-900">{uploading ? "Importazione in corso…" : "Trascina qui le fatture elettroniche XML"}</p>
        <p className="text-sm text-stone-500 mt-1">Formato FatturaPA (.xml) — puoi caricarne più di una insieme</p>
      </div>

      {vatPurch.length > 0 && (
        <div data-testid="vat-purchases" className="bg-white border border-stone-200 rounded-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-stone-700">Riepilogo IVA acquisti (imponibile fatturato)</p>
            <button data-testid="vat-purchases-export" onClick={async () => { try { const res = await api.get("/reports/vat-purchases.xlsx", { responseType: "blob" }); const u = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = u; a.download = "iva_acquisti.xlsx"; a.click(); URL.revokeObjectURL(u); } catch { toast.error("Errore export"); } }} className="text-xs font-semibold text-brand hover:underline flex items-center gap-1"><FileText size={13} /> Esporta Excel</button>
          </div>
          <table className="w-full text-xs">
            <thead><tr className="text-stone-400 text-left"><th className="py-1.5 px-2">Aliquota</th><th className="py-1.5 px-2 text-right">N. righe</th><th className="py-1.5 px-2 text-right">Imponibile</th><th className="py-1.5 px-2 text-right">IVA</th><th className="py-1.5 px-2 text-right">Totale</th></tr></thead>
            <tbody>
              {vatPurch.map((v, i) => (
                <tr key={i} data-testid={`vat-purch-row-${i}`} className="border-t border-stone-100">
                  <td className="py-1.5 px-2 text-stone-700">{formatVatRate(v.vat)}</td>
                  <td className="py-1.5 px-2 text-right text-stone-500">{v.count}</td>
                  <td className="py-1.5 px-2 text-right text-stone-700">€ {v.imponibile.toFixed(2)}</td>
                  <td className="py-1.5 px-2 text-right text-stone-600">€ {v.iva.toFixed(2)}</td>
                  <td className="py-1.5 px-2 text-right font-semibold text-stone-800">€ {v.totale.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pendingMerges.length > 0 && (
        <div data-testid="pending-merges" className="bg-violet-50 border border-violet-200 rounded-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-violet-900">Possibili duplicati da confermare ({pendingMerges.length})</p>
            <span className="flex items-center gap-2">
              <button data-testid="merge-all-btn" onClick={mergeAll} className="text-xs font-semibold text-white bg-violet-600 rounded-full px-3 py-1 hover:bg-violet-700">Unisci tutti</button>
              <button data-testid="dismiss-all-btn" onClick={dismissAll} className="text-xs font-semibold text-stone-500 border border-stone-300 rounded-full px-3 py-1 hover:bg-stone-50">Ignora tutti</button>
            </span>
          </div>
          <ul className="space-y-2">
            {pendingMerges.map((pm) => (
              <li key={pm.id} data-testid={`pending-merge-${pm.id}`} className="flex items-center justify-between gap-3 bg-white border border-violet-100 rounded-sm px-3 py-2">
                <span className="text-sm text-stone-700"><b>{pm.name}</b> potrebbe essere lo stesso di <b>{pm.candidate_name}</b> <span className="text-xs text-violet-700">(similarità {pm.ratio}%)</span></span>
                <span className="flex items-center gap-2 shrink-0">
                  <button data-testid={`merge-confirm-${pm.id}`} onClick={() => doMerge(pm.id, pm.candidate_id)} className="text-xs font-semibold text-white bg-violet-600 rounded-full px-3 py-1 hover:bg-violet-700">Unisci</button>
                  <button data-testid={`merge-dismiss-${pm.id}`} onClick={() => dismissMerge(pm.id)} className="text-xs font-semibold text-stone-500 border border-stone-300 rounded-full px-3 py-1 hover:bg-stone-50">Ignora</button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {matchLog.length > 0 && (
        <div data-testid="import-match-log" className="bg-white border border-stone-200 rounded-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-stone-700">Esito abbinamento righe importate ({matchLog.length})</p>
            <div className="flex items-center gap-3">
              <button data-testid="match-log-export" onClick={exportMatches} className="text-xs font-semibold text-brand hover:underline flex items-center gap-1"><FileText size={13} /> Esporta Excel</button>
              <button data-testid="match-log-clear" onClick={() => setMatchLog([])} className="text-xs text-stone-500 hover:text-stone-800">Chiudi</button>
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-stone-400 text-left"><th className="py-1.5 px-2">File</th><th className="py-1.5 px-2">Riga fattura</th><th className="py-1.5 px-2">Codice</th><th className="py-1.5 px-2">Esito</th><th className="py-1.5 px-2">Prodotto</th></tr></thead>
              <tbody>
                {matchLog.map((m, i) => (
                  <tr key={i} data-testid={`match-row-${i}`} className="border-t border-stone-100">
                    <td className="py-1.5 px-2 text-stone-400 whitespace-nowrap">{m.file}</td>
                    <td className="py-1.5 px-2 text-stone-600">{m.line || "—"}</td>
                    <td className="py-1.5 px-2 text-stone-500">{m.code || "—"}</td>
                    <td className="py-1.5 px-2"><span className={`inline-block px-2 py-0.5 rounded-full font-semibold ${MATCH_COLORS[m.matched_by] || "bg-stone-100 text-stone-700"}`}>{MATCH_LABELS[m.matched_by] || m.matched_by}</span></td>
                    <td className="py-1.5 px-2 text-stone-700">{m.product || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {importLog.length > 0 && (
        <div data-testid="import-log" className="bg-brand-accent/10 border border-brand-accent/30 rounded-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-stone-700">Righe scartate dai filtri import ({importLog.length})</p>
            <button data-testid="import-log-clear" onClick={() => setImportLog([])} className="text-xs text-stone-500 hover:text-stone-800">Chiudi</button>
          </div>
          <ul className="text-xs text-stone-600 space-y-1 max-h-40 overflow-y-auto">
            {importLog.map((d, i) => (
              <li key={i}><span className="text-stone-400">{d.file}:</span> {d.line || <em>(riga vuota)</em>}</li>
            ))}
          </ul>
        </div>
      )}

      <div data-testid="discards-log" className="bg-white border border-stone-200 rounded-sm">
        <button onClick={() => setShowDiscards((v) => !v)} className="w-full flex items-center justify-between px-6 py-4 text-left">
          <span className="flex items-center gap-2 font-serif text-2xl text-stone-900"><Archive size={18} className="text-brand" /> Storico righe scartate</span>
          <span className="text-sm text-stone-500">{discards.length} righe · {showDiscards ? "nascondi" : "mostra"}</span>
        </button>
        {showDiscards && (
          <div className="px-6 pb-6">
            {discards.length === 0 ? <p className="text-sm text-stone-400">Nessuna riga scartata registrata.</p> : (
              <>
                <div className="flex justify-end mb-3">
                  <button data-testid="clear-discards-btn" onClick={() => setConfirmClear(true)} className="text-xs text-alert hover:underline flex items-center gap-1"><Trash2 size={13} /> Svuota storico</button>
                </div>
                <div className="max-h-72 overflow-y-auto border border-stone-100 rounded-sm">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-stone-50"><tr className="text-stone-500"><th className="text-left py-2 px-3 font-bold">Data</th><th className="text-left py-2 px-3 font-bold">Fornitore</th><th className="text-left py-2 px-3 font-bold">Fattura</th><th className="text-left py-2 px-3 font-bold">Riga scartata</th></tr></thead>
                    <tbody>{discards.map((d, i) => <tr key={i} className="border-t border-stone-100"><td className="py-1.5 px-3 text-stone-500 whitespace-nowrap">{new Date(d.at).toLocaleDateString("it-IT")}</td><td className="py-1.5 px-3 text-stone-700">{d.supplier || "—"}</td><td className="py-1.5 px-3 text-stone-600">{d.invoice_number || "—"}</td><td className="py-1.5 px-3 text-stone-600">{d.line || <em>(vuota)</em>}</td></tr>)}</tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {user?.role === "owner" && (
        <div data-testid="company-filters" className="bg-white border border-stone-200 rounded-sm p-6">
          <div className="flex items-center gap-2 mb-2"><Filter size={18} className="text-brand" /><h3 className="font-serif text-2xl text-stone-900">Filtri import azienda</h3></div>
          <p className="text-sm text-stone-500 mb-4">Le righe che contengono questi testi verranno scartate durante l'import, in aggiunta ai filtri globali.</p>
          {gFilters.length > 0 && <p className="text-xs text-stone-400 mb-3">Filtri globali attivi: {gFilters.join(", ")}</p>}
          <div className="flex gap-2 mb-4">
            <input data-testid="cf-new-input" value={cfNew} onChange={(e) => setCfNew(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCF()}
              placeholder="Nuovo filtro azienda (es. omaggio)" className="flex-1 border border-stone-300 bg-white px-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40" />
            <button data-testid="cf-add-btn" onClick={addCF} className="rounded-full bg-brand text-white px-5 py-2 text-sm font-semibold flex items-center gap-2 hover:bg-brand-hover"><Plus size={16} /> Aggiungi</button>
          </div>
          {cFilters.length === 0 ? <p className="text-sm text-stone-400">Nessun filtro specifico per la tua azienda.</p> : (
            <ul className="space-y-2" data-testid="cf-list">
              {cFilters.map((p, i) => (
                <li key={i} className="flex items-center gap-2" data-testid={`cf-row-${i}`}>
                  <input data-testid={`cf-input-${i}`} value={p} onChange={(e) => setCFilters(cFilters.map((x, idx) => idx === i ? e.target.value : x))} onBlur={() => saveCF(cFilters)}
                    className="flex-1 border border-stone-300 bg-white px-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40" />
                  <button data-testid={`cf-del-${i}`} onClick={() => removeCF(i)} className="text-stone-400 hover:text-alert p-2"><Trash2 size={16} /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {invoices.length > 0 && (
        <div data-testid="acq-reparto-filter" className="flex flex-wrap gap-2 items-center">
          <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} onClear={() => { setFrom(""); setTo(""); }} />
          <select data-testid="supplier-filter" value={supplierFilter} onChange={(e) => setSupplierFilter(e.target.value)}
            className="border border-stone-300 bg-white px-3 py-1.5 text-xs rounded-full outline-none focus:ring-2 focus:ring-brand/40">
            <option value="">Tutti i fornitori</option>
            {suppliers.map((sname) => <option key={sname} value={sname}>{sname}</option>)}
          </select>
          <span className="w-px h-5 bg-stone-200" />
          <button data-testid="filter-all" onClick={() => setFilterCat("")} className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${!filterCat ? "bg-brand text-white border-brand" : "bg-white border-stone-300 text-stone-600 hover:bg-stone-50"}`}>Tutti</button>
          {cats.map((c) => (
            <button key={c} data-testid={`filter-${c}`} onClick={() => setFilterCat(filterCat === c ? "" : c)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1.5 ${filterCat === c ? "text-white border-transparent" : "bg-white border-stone-300 text-stone-600 hover:bg-stone-50"}`}
              style={filterCat === c ? { background: catColors[c] || "#2C4A3B" } : undefined}>
              <span className="w-2 h-2 rounded-full" style={{ background: catColors[c] || "#A8A29E" }} />{c}
            </button>
          ))}
        </div>
      )}

      <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-stone-200 text-xs uppercase tracking-[0.12em] text-stone-500">
              <th className="text-left font-bold py-4 px-6">Fornitore</th>
              <th className="text-left font-bold py-4 px-6">Numero</th>
              <th className="text-left font-bold py-4 px-6">Data</th>
              <th className="text-left font-bold py-4 px-6">Righe</th>
              <th className="text-right font-bold py-4 px-6">Totale</th>
              <th className="py-4 px-6"></th>
            </tr>
          </thead>
          <tbody>
            {filteredInvoices.length === 0 && (
              <tr><td colSpan={6} className="py-16 text-center text-stone-400">
                <FileText size={28} strokeWidth={1.3} className="mx-auto mb-2" />
                Nessun acquisto ancora. Carica un XML o inserisci manualmente.
              </td></tr>
            )}
            {pageInvoices.map((inv) => (
              <Fragment key={inv.id}>
                <tr data-testid={`invoice-row-${inv.id}`}
                  className="border-b border-stone-100 hover:bg-stone-50 transition-colors cursor-pointer"
                  onClick={() => setExpanded(expanded === inv.id ? null : inv.id)}>
                  <td className="py-4 px-6 font-semibold text-stone-800">{inv.supplier_name}</td>
                  <td className="py-4 px-6 text-stone-600">{inv.invoice_number}</td>
                  <td className="py-4 px-6 text-stone-600">{formatDate(inv.invoice_date)}</td>
                  <td className="py-4 px-6 text-stone-600">
                    <span className="inline-flex items-center gap-1">{inv.lines.length}
                      <ChevronDown size={14} className={`transition-transform ${expanded === inv.id ? "rotate-180" : ""}`} /></span>
                  </td>
                  <td className="py-4 px-6 text-right font-bold text-stone-900">{euro(inv.total_amount)}</td>
                  <td className="py-4 px-6 text-right">
                    <button data-testid={`delete-invoice-${inv.id}`} onClick={(e) => { e.stopPropagation(); del(inv.id); }} disabled={deletingId === inv.id}
                      className="text-stone-400 hover:text-alert transition-colors disabled:opacity-40 disabled:cursor-not-allowed"><Trash2 size={16} /></button>
                  </td>
                </tr>
                {expanded === inv.id && (
                  <tr className="bg-stone-50/60"><td colSpan={6} className="px-6 py-4">
                    <table className="w-full text-xs">
                      <thead><tr className="text-stone-400 uppercase tracking-wider">
                        <th className="text-left py-2">Prodotto</th><th className="text-right py-2">Q.tà</th>
                        <th className="text-right py-2">Prezzo unit.</th><th className="text-right py-2">Totale</th>
                      </tr></thead>
                      <tbody>
                        {inv.lines.map((l, i) => (
                          <tr key={`${l.normalized_name || l.description}-${i}`} className="border-t border-stone-100">
                            <td className="py-2 text-stone-700"><span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full shrink-0" style={{ background: catColors[catMap[l.normalized_name]] || "#D6D3D1" }} />{l.description}</span></td>
                            <td className="py-2 text-right text-stone-600">{l.quantity} {l.unit}</td>
                            <td className="py-2 text-right text-stone-600">{price(l.unit_price)}</td>
                            <td className="py-2 text-right font-semibold text-stone-800">{euro(l.total_price)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </td></tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {filteredInvoices.length > PAGE_SIZE && (
        <div data-testid="acq-pagination" className="flex items-center justify-between gap-3 flex-wrap">
          <span className="text-xs text-stone-500">
            {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredInvoices.length)} di {filteredInvoices.length} acquisti
          </span>
          <div className="flex items-center gap-2">
            <button data-testid="page-prev" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage <= 1}
              className="text-xs font-semibold rounded-full border border-stone-300 text-stone-600 px-3 py-1.5 hover:bg-stone-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">Precedente</button>
            <span className="text-xs text-stone-500">Pagina {currentPage} di {totalPages}</span>
            <button data-testid="page-next" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}
              className="text-xs font-semibold rounded-full border border-stone-300 text-stone-600 px-3 py-1.5 hover:bg-stone-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">Successiva</button>
          </div>
        </div>
      )}

      {showManual && <ManualModal onClose={() => setShowManual(false)} onSaved={() => { setShowManual(false); load(); loadDiscards(); }} />}
      {confirmClear && <ConfirmDialog open title="Svuotare lo storico scarti?" description="Tutte le righe scartate registrate verranno eliminate. L'operazione non è reversibile." confirmLabel="Svuota" onConfirm={async () => { await api.delete("/import/discards"); toast.success("Storico svuotato"); setConfirmClear(false); loadDiscards(); }} onClose={() => setConfirmClear(false)} />}
    </div>
  );
}

const newLine = () => ({ id: `l_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, description: "", quantity: 1, unit: "", unit_price: "", total_price: "" });

function ManualModal({ onClose, onSaved }) {
  const [supplier, setSupplier] = useState("");
  const [vat, setVat] = useState("");
  const [number, setNumber] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [lines, setLines] = useState([newLine()]);
  const [saving, setSaving] = useState(false);

  const setLine = (i, key, val) => {
    const copy = [...lines];
    copy[i][key] = val;
    if (key === "quantity" || key === "unit_price") {
      const q = parseFloat(copy[i].quantity) || 0;
      const p = parseFloat(copy[i].unit_price) || 0;
      copy[i].total_price = (q * p).toFixed(2);
    }
    setLines(copy);
  };

  const save = async () => {
    if (!supplier || !number) { toast.error("Compila fornitore e numero"); return; }
    setSaving(true);
    try {
      const { data: saved } = await api.post("/invoices", {
        supplier_name: supplier, supplier_vat: vat, invoice_number: number, invoice_date: date,
        lines: lines.filter((l) => l.description).map((l) => ({
          description: l.description, quantity: parseFloat(l.quantity) || 1, unit: l.unit,
          unit_price: parseFloat(l.unit_price) || 0, total_price: parseFloat(l.total_price) || 0,
        })),
      });
      const cs = saved.catalog_summary || {};
      toast.success(`Acquisto salvato — ${cs.new || 0} nuovi, ${cs.updated || 0} aggiornati in Anagrafica`);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div data-testid="manual-modal" className="bg-white rounded-sm w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-stone-200 sticky top-0 bg-white">
          <h3 className="font-serif text-2xl text-stone-900">Nuovo acquisto manuale</h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-800"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Fornitore"><input data-testid="manual-supplier" value={supplier} onChange={(e) => setSupplier(e.target.value)} className={inp} /></Field>
            <Field label="P. IVA"><input value={vat} onChange={(e) => setVat(e.target.value)} className={inp} /></Field>
            <Field label="Numero fattura"><input data-testid="manual-number" value={number} onChange={(e) => setNumber(e.target.value)} className={inp} /></Field>
            <Field label="Data"><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inp} /></Field>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Righe prodotto</span>
              <button data-testid="add-line-btn" onClick={() => setLines([...lines, newLine()])}
                className="text-brand text-sm flex items-center gap-1 hover:underline"><Plus size={14} /> Aggiungi</button>
            </div>
            <div className="space-y-2">
              {lines.map((l, i) => (
                <div key={l.id} className="grid grid-cols-12 gap-2 items-center">
                  <input placeholder="Descrizione" data-testid={`line-desc-${i}`} value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} className={`${inp} col-span-5`} />
                  <input placeholder="Q.tà" type="number" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} className={`${inp} col-span-2`} />
                  <input placeholder="Prezzo un." type="number" data-testid={`line-price-${i}`} value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} className={`${inp} col-span-2`} />
                  <input placeholder="Totale" type="number" value={l.total_price} onChange={(e) => setLine(i, "total_price", e.target.value)} className={`${inp} col-span-2`} />
                  <button onClick={() => setLines(lines.filter((_, j) => j !== i))} className="text-stone-400 hover:text-alert col-span-1 flex justify-center"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="p-6 border-t border-stone-200 flex justify-end gap-3 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-5 py-2.5 text-sm text-stone-600 rounded-none hover:bg-stone-100">Annulla</button>
          <button data-testid="save-manual-btn" onClick={save} disabled={saving}
            className="rounded-full bg-brand text-white px-6 py-2.5 text-sm font-semibold hover:bg-brand-hover transition-colors disabled:opacity-60">
            {saving ? "Salvataggio…" : "Salva acquisto"}
          </button>
        </div>
      </div>
    </div>
  );
}

const inp = "w-full border border-stone-300 bg-white px-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40 transition-colors";
function Field({ label, children }) {
  return (<div><label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">{label}</label><div className="mt-1.5">{children}</div></div>);
}
